import {
  updateMarkingCriteriaPreferences,
  updateCommentBankPreferences,
  getAllMarkingCriteriaWithPreferences,
  getAllMarkingCriteria,
  getAllCommentBanksWithPreferences,
  getTeachersCommentBanks,
  deleteSmartAnnotation,
  deleteMarkingCriteria,
  updateFeedbackBanks,
  getAllSuccessCriteria,
  getAllSuccessCriteriaWithPreferences,
  deleteSuccessCriteria,
  updateSuccessCriteriaPreferences,
  getAllShortAnswers,
  getAllShortAnswersWithPreferences,
  deleteShortAnswer,
  updateShortAnswerPreferences,
} from '../../../service';

const buildFilters = (params = {}) => {
  const subjects = params.subjects ?? [];
  return {
    years: params.years ?? [],
    subjects,
    subjectCodes: params.subjectCodes ?? subjects,
    subjectCode: subjects.length === 1 ? subjects[0] : undefined,
    state: params.state,
    taskTypes: params.taskTypes ?? [],
    responseTypes: params.responseTypes ?? [],
    sortOrder: params.sortOrder === 'newToOld' || params.sortOrder === 'oldToNew' ? params.sortOrder : undefined,
  };
};

export const feedbackToolApi = {
  async getItems(params = {}, context = 'markingCriteria') {
    const filters = buildFilters(params);

    if (context === 'commentBanks') {
      const [commentBanksData, commentBanksWithPreferences] = await Promise.all([
        getTeachersCommentBanks(filters),
        getAllCommentBanksWithPreferences()
      ]);

      const originalCommentBanks = commentBanksData?.commentBanks ?? [];
      const mergedCommentBanks = originalCommentBanks.map(bank => {
        const preference = commentBanksWithPreferences?.find(pref => pref.id === bank.id);
        const visible = preference ? (preference.visible !== false) : true;
        const isVisible = preference ? (preference.isVisible !== false) : true;
        return preference ? {
          ...bank,
          isVisible,
          visible,
          sortOrder: preference.sortOrder ?? bank.sortOrder
        } : { ...bank, isVisible: true, visible: true };
      });

      return {
        teacherId: commentBanksData?.teacherId || 'unknown',
        commentBanks: mergedCommentBanks
      };
    } else if (context === 'successCriteria') {
      const [successCriteria, successCriteriaWithPreferences] = await Promise.all([
        getAllSuccessCriteria(filters),
        getAllSuccessCriteriaWithPreferences()
      ]);

      const originalSuccessCriteria = Array.isArray(successCriteria) ? successCriteria : [];
      const successCriteriaPreferences = Array.isArray(successCriteriaWithPreferences) ? successCriteriaWithPreferences : [];

      const mergedSuccessCriteria = originalSuccessCriteria.map(criteria => {
        const preference = successCriteriaPreferences.find(pref => pref.id === criteria.id);
        const merged = preference ? {
          ...criteria,
          isVisible: preference.isVisible,
          visible: preference.visible !== false && preference.isVisible !== false,
          sortOrder: preference.sortOrder
        } : criteria;
        return merged;
      });

      return { successCriteria: mergedSuccessCriteria };
    } else if (context === 'shortAnswers') {
      const [shortAnswersData, shortAnswersWithPreferences] = await Promise.all([
        getAllShortAnswers(filters),
        getAllShortAnswersWithPreferences()
      ]);

      const originalShortAnswers = Array.isArray(shortAnswersData) ? shortAnswersData : [];
      const mergedShortAnswers = originalShortAnswers.map((answer) => {
        const preference = shortAnswersWithPreferences?.find(pref => pref.id === answer.id);
        const merged = preference ? {
          ...answer,
          isVisible: preference.isVisible,
          visible: preference.visible !== false && preference.isVisible !== false,
          sortOrder: preference.sortOrder
        } : answer;
        return merged;
      });

      return { shortAnswers: mergedShortAnswers };
    } else {
      const [markingCriterias, preferences] = await Promise.all([
        getAllMarkingCriteria(filters),
        getAllMarkingCriteriaWithPreferences()
      ]);
      return { markingCriterias, preferences };
    }
  },

  async getItemsCount(params = {}, context = 'markingCriteria') {
    const items = await this.getItems(params, context);
    if (context === 'commentBanks') {
      if (items?.commentBanks && Array.isArray(items.commentBanks)) {
        return items.commentBanks.length;
      } else if (Array.isArray(items)) {
        return items.length;
      }
      return 0;
    } else if (context === 'successCriteria') {
      if (items?.successCriteria && Array.isArray(items.successCriteria)) {
        return items.successCriteria.length;
      }
      return 0;
    } else if (context === 'shortAnswers') {
      if (items?.shortAnswers && Array.isArray(items.shortAnswers)) {
        return items.shortAnswers.length;
      }
      return 0;
    } else {
      return items.preferences ? items.preferences.length : 0;
    }
  },

  async getItem(id, context = 'markingCriteria') {
    const items = await this.getItems({}, context);
    if (context === 'commentBanks') {
      if (items?.commentBanks && Array.isArray(items.commentBanks)) {
        return items.commentBanks.find(item => item.id === id) || null;
      } else if (Array.isArray(items)) {
        return items.find(item => item.id === id) || null;
      }
      return null;
    } else if (context === 'successCriteria') {
      if (items?.successCriteria && Array.isArray(items.successCriteria)) {
        return items.successCriteria.find(item => item.id === id) || null;
      }
      return null;
    } else if (context === 'shortAnswers') {
      if (items?.shortAnswers && Array.isArray(items.shortAnswers)) {
        return items.shortAnswers.find(item => item.id === id) || null;
      }
      return null;
    } else {
      const markingCriteria = items.markingCriterias?.find(item => item.id === id);
      const preference = items.preferences?.find(item => item.id === id);
      return markingCriteria || preference || null;
    }
  },

  async reorderItems(itemId, newSortOrder, context = 'markingCriteria') {
    try {
      if (context === 'commentBanks') {
        const result = await updateCommentBankPreferences('REORDER', {
          toolIds: [itemId],
          newSortOrder: newSortOrder
        });
        return result;
      } else if (context === 'successCriteria') {
        const result = await updateSuccessCriteriaPreferences('REORDER', {
          toolIds: [itemId],
          newSortOrder: newSortOrder
        });
        return result;
      } else if (context === 'shortAnswers') {
        const result = await updateShortAnswerPreferences('REORDER', {
          toolIds: [itemId],
          newSortOrder: newSortOrder
        });
        return result;
      } else if (context === 'smartComments') {
        const updateData = {
          smartComments: newSortOrder
        };
        const result = await updateFeedbackBanks(updateData, itemId);
        return result;
      } else {
        const result = await updateMarkingCriteriaPreferences('REORDER', {
          toolIds: [itemId],
          newSortOrder: newSortOrder
        });
        return result;
      }
    } catch (error) {
      throw error;
    }
  },

  async hideItems(itemIds, context = 'markingCriteria') {
    try {
      if (!itemIds || !Array.isArray(itemIds) || itemIds.length === 0) {
        throw new Error('Invalid itemIds: must be a non-empty array');
      }

      if (context === 'commentBanks') {
        return await updateCommentBankPreferences('HIDE', {
          toolIds: itemIds,
          newSortOrder: null
        });
      } else if (context === 'successCriteria') {
        return await updateSuccessCriteriaPreferences('HIDE', {
          toolIds: itemIds,
          newSortOrder: null
        });
      } else if (context === 'shortAnswers') {
        return await updateShortAnswerPreferences('HIDE', {
          toolIds: itemIds,
          newSortOrder: null
        });
      } else {
        return await updateMarkingCriteriaPreferences('HIDE', {
          toolIds: itemIds,
          newSortOrder: null
        });
      }
    } catch (error) {
      console.error('Error in hideItems API call:', error);
      console.error('Context:', context, 'ItemIds:', itemIds);
      throw error;
    }
  },

  async showItems(itemIds, context = 'markingCriteria') {
    if (context === 'commentBanks') {
      return await updateCommentBankPreferences('SHOW', {
        toolIds: itemIds,
        newSortOrder: null
      });
    } else if (context === 'successCriteria') {
      return await updateSuccessCriteriaPreferences('SHOW', {
        toolIds: itemIds,
        newSortOrder: null
      });
    } else if (context === 'shortAnswers') {
      return await updateShortAnswerPreferences('SHOW', {
        toolIds: itemIds,
        newSortOrder: null
      });
    } else {
      return await updateMarkingCriteriaPreferences('SHOW', {
        toolIds: itemIds,
        newSortOrder: null
      });
    }
  },

  async moveToTop(itemIds, context = 'markingCriteria') {
    try {
      if (context === 'commentBanks') {
        const result = await updateCommentBankPreferences('MOVE_TO_TOP', {
          toolIds: itemIds,
          newSortOrder: null
        });
        return result;
      } else if (context === 'successCriteria') {
        const result = await updateSuccessCriteriaPreferences('MOVE_TO_TOP', {
          toolIds: itemIds,
          newSortOrder: null
        });
        return result;
      } else if (context === 'shortAnswers') {
        const result = await updateShortAnswerPreferences('MOVE_TO_TOP', {
          toolIds: itemIds,
          newSortOrder: null
        });
        return result;
      } else {
        const result = await updateMarkingCriteriaPreferences('MOVE_TO_TOP', {
          toolIds: itemIds,
          newSortOrder: null
        });
        return result;
      }
    } catch (error) {
      console.error('API Debug - moveToTop error:', error);
      console.error('API Debug - moveToTop error details:', {
        itemIds,
        context,
        errorMessage: error.message,
        errorStack: error.stack
      });
      throw error;
    }
  },

  async deleteItems({ itemsToDelete, systemItems }, context = 'markingCriteria') {
    if (context === 'commentBanks') {
      if (!itemsToDelete || itemsToDelete.length === 0) {
        return;
      }

      try {
        await updateCommentBankPreferences('DELETE', {
          toolIds: itemsToDelete,
          newSortOrder: null
        });

        for (const itemId of itemsToDelete) {
          await deleteSmartAnnotation(itemId);
        }
      } catch (error) {
        console.error('Error in delete items for comment banks:', error);
        throw error;
      }
    } else if (context === 'successCriteria') {
      if (!itemsToDelete || itemsToDelete.length === 0) {
        return;
      }

      try {
        await updateSuccessCriteriaPreferences('DELETE', {
          toolIds: itemsToDelete,
          newSortOrder: null
        });

        for (const itemId of itemsToDelete) {
          await deleteSuccessCriteria(itemId);
        }
      } catch (error) {
        console.error('Error in delete items for success criteria:', error);
        throw error;
      }
    } else if (context === 'shortAnswers') {
      if (!itemsToDelete || itemsToDelete.length === 0) {
        return;
      }

      try {
        await updateShortAnswerPreferences('DELETE', {
          toolIds: itemsToDelete,
          newSortOrder: null
        });

        for (const itemId of itemsToDelete) {
          await deleteShortAnswer(itemId);
        }
      } catch (error) {
        console.error('Error in delete items for short answers:', error);
        throw error;
      }
    } else {
      if (!itemsToDelete || itemsToDelete.length === 0) {
        return;
      }

      try {
        for (const itemId of itemsToDelete) {
          await deleteMarkingCriteria(itemId);
        }
      } catch (error) {
        console.error('Error in delete items for marking criteria:', error);
        throw error;
      }
    }
  },
}; 